import Heading from '@ilg-totallymoney/ui/components/Heading'
import Stack from '@ilg-totallymoney/ui/components/Stack'
import Text from '@ilg-totallymoney/ui/components/Text'

import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

const buildCreditCardInformationJsx = ({
    introductionText,
    subheading,
    subsectionText,
}: {
    introductionText: string
    subheading: string
    subsectionText: string[]
}) => (
    <Stack space="space50">
        <Text color="light" variant="120">
            {introductionText}
        </Text>

        <Stack space="space20">
            <Heading variant="80">{subheading}</Heading>

            {subsectionText.map((text, i) => (
                <Text color="light" variant="120" key={`subsection-${i}`}>
                    {text}
                </Text>
            ))}
        </Stack>
    </Stack>
)

export const creditCardInformation: Record<
    Partial<CreditCardProductCategory>,
    React.ReactNode
> = {
    [CreditCardProductCategory.BalanceTransfer]: buildCreditCardInformationJsx({
        introductionText:
            'Balance transfer credit cards let you move existing credit card debt to a new provider, so you can stop paying interest for a set period of time. With almost half of credit card customers paying interest each month, a balance transfer could help you focus on clearing debt rather than paying interest.',
        subheading: 'How do balance transfer credit cards work?',
        subsectionText: [
            'You move your existing credit card debt to a new card which offers 0% interest for a fixed period of time. You’ll usually pay a transfer fee of 2-4%, but this one-off cost is often much less than the interest you’d pay. Once you’ve transferred the balance, you can focus on paying off the debt without interest eating into your repayments.',
        ],
    }),

    [CreditCardProductCategory.BestCards]: buildCreditCardInformationJsx({
        introductionText:
            'Credit cards let buy things with borrowed money, which you then pay back over time. Each month you’ll get a statement which shows you what you owe, and the minimum you need to repay. Clear the full balance and you’ll never pay interest.',
        subheading: 'How do credit cards work?',
        subsectionText: [
            'When you use a credit card, the card provider pays for your purchase. You then repay this money, either in full or over time. If you don’t clear the balance by the due date, you’ll pay interest on what you owe.',
            'Most cards give you up to 56 days interest-free if you pay off the full balance each month. But if you make the minimum payment, interest can build up, making purchases more expensive over time.',
        ],
    }),

    [CreditCardProductCategory.Cashback]: buildCreditCardInformationJsx({
        introductionText:
            'Cashback credit cards put money back in your pocket when you spend. No points or vouchers to track and exchange, just real cash. So, using one for everyday purchases and making sure you clear the balance in full each month can be an easy way of getting a bit for your money.',
        subheading: 'How do cashback credit cards work?',
        subsectionText: [
            'Every time you use your card, you’ll earn a percentage of what you spend back. This could be a flat rate on everything you buy, or it could change depending on where you’re spending. Any cashback is usually paid into your account either monthly or annually.',
            'Card providers are usually able to offer cashback to customers because they charge retailers a fee when taking payments.',
        ],
    }),

    [CreditCardProductCategory.Combined]: buildCreditCardInformationJsx({
        introductionText:
            'Balance transfer and purchase credit cards let you move expensive debt from other providers while also making new purchases – without paying any interest. And instead of needing to have different cards for different needs, you can keep everything in one place.',
        subheading: 'How do balance transfer and purchase credit cards work?',
        subsectionText: [
            'Sometimes known as combination (or combo) cards, these offer 0% interest on both balance transfers and new purchases for a set amount of time.',
            'One thing to remember is that the 0% periods for transfers and purchases are often different lengths. And with one lasting longer than the other, you’ll want to make sure you don’t get caught out and start paying interest on what you owe.',
        ],
    }),

    [CreditCardProductCategory.PoorCredit]: buildCreditCardInformationJsx({
        introductionText:
            'Credit cards for poor credit are designed for people who might have damaged credit files, or have been turned down previously. They give you a chance to show lenders you can manage credit responsibly, helping you to rebuild your credit file step by step.',
        subheading: 'How do credit cards for poor credit work?',
        subsectionText: [
            'They often come with higher interest rates, lower limits, no interest-free periods.',
            'However, if you pay off your bill in full each month, you won’t pay any interest. As you rebuild your score, you should be able to borrow more money, at lower rates, and unlock other perks along the way.',
        ],
    }),

    [CreditCardProductCategory.Purchase]: buildCreditCardInformationJsx({
        introductionText:
            '0% purchase credit cards let you buy things today, and pay them off over a set number of months without paying any interest. With the cost of living hitting household budgets, 0% purchase cards can help you manage your cash flow and buy what you need when money’s tight.',
        subheading: 'How do 0% purchase credit cards work?',
        subsectionText: [
            'When you use a 0% purchase card to buy something, you won’t pay any interest on that purchase for a set period of time. It’s important to remember that you’ll still need to make at least the minimum monthly payment, but because you’re not paying interest, every penny will go towards paying off what you actually spent.',
            'Once the 0% period ends, you’ll start paying interest on what you owe, at the cards standard interest rate – so try to clear your balance before the end of the offer.',
        ],
    }),

    [CreditCardProductCategory.Rewards]: buildCreditCardInformationJsx({
        introductionText:
            'With a rewards credit card, you get something extra back when you spend. That might be cashback that goes straight back into your account, points you use for shopping, or air miles that can go towards your holidays.',
        subheading: 'How do rewards credit cards work?',
        subsectionText: [
            'Every time you use a rewards card, you’ll earn something back - usually cashback (a percentage of what you spend) or points that you can exchange for vouchers, travel, or other rewards. Some cards also throw in extra perks like airport lounge access or exclusive shopping discounts.',
        ],
    }),

    [CreditCardProductCategory.Travel]: buildCreditCardInformationJsx({
        introductionText:
            'Travel credit cards are designed for spending abroad, and unlike regular cards, they won’t charge you extra fees when shopping overseas or buying from foreign websites. You’ll often get better exchange rates, which means your money goes further.',
        subheading: 'How do travel credit cards work?',
        subsectionText: [
            'When you use a regular credit or debit card abroad, you’ll usually pay a foreign transaction fee, plus you find yourself paying extra for ATM withdrawals. Travel cards aim to cut out these fees, giving you a better exchange rate without the mark-up.',
            'Some travel cards will also let you withdraw cash fee-free at ATMs, but these can sometimes count as cash advances, and you might start paying interest straight away – so it’s best to just use the card for purchases when you can.',
        ],
    }),
}
